import React, { useState, useEffect, useRef, useContext } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { CourseEditorContext } from '../../../../../contexts/CourseEditorContext';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import FormatTextdirectionLToRIcon from '@mui/icons-material/FormatTextdirectionLToR';
import FormatTextdirectionRToLIcon from '@mui/icons-material/FormatTextdirectionRToL';
import './ChapterEditor.scss';

export const BulletsComponent = ({
    content,
    onContentChange,
    isSelected,
    onSelect,
    id,
    fontSize,
    fontWeight,
    fontStyle,
    textDecoration,
    color,
    borderRadius,
    backgroundColor,
    opacity = 1,
    direction = 'rtl',
    mode = "edit",
    onAnimationComplete,
    isSequentialAnimationActive = false,
    onOpenEditorDrawer
}) => {
    const { i18n } = useContext(I18nReactContext);
    const courseEditorContext = useContext(CourseEditorContext);
    const [isEditing, setIsEditing] = useState(false);
    const [editingBulletIndex, setEditingBulletIndex] = useState(-1);
    const [bullets, setBullets] = useState([]);
    const [textDirection, setTextDirection] = useState(direction);
    const [displayedBullets, setDisplayedBullets] = useState([]);
    const [animatingBullets, setAnimatingBullets] = useState(new Set());
    const textareaRef = useRef(null);
    const animationTimeoutsRef = useRef(new Map());

    // Parse content into bullets array when content changes
    useEffect(() => {
        if (content) {
            try {
                const parsedContent = JSON.parse(content);
                setBullets(Array.isArray(parsedContent) ? parsedContent : []);
            } catch (e) {
                // If content is not valid JSON, initialize with a single empty bullet
                setBullets([{ text: content || '' }]);
            }
        } else {
            // Initialize with a single empty bullet if no content
            setBullets([{ text: '' }]);
        }
    }, [content]);

    // Update direction when the prop changes
    useEffect(() => {
        setTextDirection(direction);
    }, [direction]);

    // Animation Effect - handles both typing and resize animations based on mode
    useEffect(() => {
        const isAIGenerating = courseEditorContext?.isGeneratingChapter;
        const isShowingAIAnimations = courseEditorContext?.isShowingAIChapterAnimations;
        const aiGenerationStatus = courseEditorContext?.aiGenerationStatus?.status;

        // Determine animation type based on the three scenarios:
        // 1. View mode for student: no typing animations, resize animation for all components
        // 2. AI not currently generating: no typing animations, resize animation for all components  
        // 3. AI currently generating: typing animation for text/bullets, resize for others

        const isStudentViewMode = mode === "view";
        const isAICurrentlyGenerating = isAIGenerating || (aiGenerationStatus === 'IN_PROGRESS');

        // Show typing animation only when:
        // - NOT in student view mode AND
        // - (AI is currently generating OR sequential animations are active) AND
        // - We have bullets AND
        // - AI generation is not completed
        const shouldShowTypingAnimation = !isStudentViewMode && 
                                        (isAICurrentlyGenerating || isSequentialAnimationActive) && 
                                        bullets.length > 0 && 
                                        aiGenerationStatus !== 'COMPLETED';

        if (shouldShowTypingAnimation) {
            // Clear any existing animations
            animationTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
            animationTimeoutsRef.current.clear();

            // Initialize displayed bullets with empty text
            const initialDisplayedBullets = bullets.map(() => ({ text: '' }));
            setDisplayedBullets(initialDisplayedBullets);
            setAnimatingBullets(new Set());

            // Animate bullets sequentially - each bullet starts only after the previous one completes
            const animateBulletsSequentially = (bulletIndex = 0) => {
                if (bulletIndex >= bullets.length) {
                    // All bullets completed
                    if (isSequentialAnimationActive && onAnimationComplete) {
                        console.log('BulletsComponent all animations completed for:', id);
                        setTimeout(() => onAnimationComplete(id), 50);
                    }
                    return;
                }

                const bullet = bullets[bulletIndex];
                if (bullet.text) {
                    setAnimatingBullets(prev => new Set([...prev, bulletIndex]));

                    let currentIndex = 0;
                    const animateText = () => {
                        if (currentIndex < bullet.text.length) {
                            setDisplayedBullets(prev => {
                                const newDisplayed = [...prev];
                                newDisplayed[bulletIndex] = {
                                    ...newDisplayed[bulletIndex],
                                    text: bullet.text.substring(0, currentIndex + 1)
                                };
                                return newDisplayed;
                            });
                            currentIndex++;
                            const timeout = setTimeout(animateText, 20); // 20ms per character
                            animationTimeoutsRef.current.set(`${bulletIndex}-${currentIndex}`, timeout);
                        } else {
                            // Current bullet finished, remove from animating set
                            setAnimatingBullets(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(bulletIndex);
                                return newSet;
                            });

                            // Start the next bullet after a brief delay
                            const timeout = setTimeout(() => animateBulletsSequentially(bulletIndex + 1), 100);
                            animationTimeoutsRef.current.set(`next-bullet-${bulletIndex}`, timeout);
                        }
                    };

                    // Start animation after a brief delay
                    const timeout = setTimeout(animateText, 100);
                    animationTimeoutsRef.current.set(`${bulletIndex}-start`, timeout);
                } else {
                    // Skip empty bullets and move to next
                    animateBulletsSequentially(bulletIndex + 1);
                }
            };

            // Start the sequential animation with the first bullet
            animateBulletsSequentially(0);
        } else {
            // For all other scenarios (student view, AI not generating, AI completed):
            // Show full content immediately and let CSS handle resize animation
            setDisplayedBullets(bullets);
            setAnimatingBullets(new Set());

            // If sequential animations are active but we're not showing typing animation,
            // immediately call completion callback
            if (isSequentialAnimationActive && onAnimationComplete && bullets.length > 0) {
                console.log('BulletsComponent immediately completing for:', id);
                setTimeout(() => onAnimationComplete(id), 50);
            }
        }

        // Cleanup function
        return () => {
            animationTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
            animationTimeoutsRef.current.clear();
        };
    }, [bullets, courseEditorContext?.isGeneratingChapter, courseEditorContext?.isShowingAIChapterAnimations, courseEditorContext?.aiGenerationStatus?.status, isEditing, mode, isSequentialAnimationActive, onAnimationComplete, id]);

    // Focus the textarea when editing starts
    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isEditing, editingBulletIndex]);

    // Toggle text direction
    const toggleDirection = () => {
        const newDirection = textDirection === 'ltr' ? 'rtl' : 'ltr';
        setTextDirection(newDirection);
        // Notify parent component of direction change
        if (onContentChange) {
            onContentChange(JSON.stringify(bullets), { direction: newDirection });
        }
    };

    const handleClick = (e) => {
        e.stopPropagation();
        if (!isSelected) {
            onSelect(id);
        }
    };

    // Double-click opens the editor drawer
    const handleDoubleClick = (e) => {
        e.stopPropagation();
        if (mode === "edit" && onOpenEditorDrawer) {
            onOpenEditorDrawer({
                id,
                type: 'bullets',
                content,
                fontSize,
                fontWeight,
                fontStyle,
                textDecoration,
                color,
                borderRadius,
                backgroundColor,
                opacity,
                direction
            });
        }
    };

    const handleChange = (e) => {
        const newBullets = [...bullets];
        newBullets[editingBulletIndex] = { 
            ...newBullets[editingBulletIndex], 
            text: e.target.value 
        };
        setBullets(newBullets);
    };

    const handleBlur = () => {
        setIsEditing(false);
        setEditingBulletIndex(-1);
        // Save the updated bullets to the parent component
        onContentChange(JSON.stringify(bullets), { direction: textDirection });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            // Ctrl+Enter to save and exit editing
            setIsEditing(false);
            setEditingBulletIndex(-1);
            onContentChange(JSON.stringify(bullets), { direction: textDirection });
        } else if (e.key === 'Escape') {
            // Escape to cancel editing
            setIsEditing(false);
            setEditingBulletIndex(-1);
            // Revert to the original content
            if (content) {
                try {
                    const parsedContent = JSON.parse(content);
                    setBullets(Array.isArray(parsedContent) ? parsedContent : []);
                } catch (e) {
                    setBullets([{ text: content || '' }]);
                }
            } else {
                setBullets([{ text: '' }]);
            }
        } else if ((e.key === 'Shift' && e.altKey) || (e.key === 'Alt' && e.shiftKey)) {
            // Alt+Shift to toggle text direction
            e.preventDefault();
            toggleDirection();
        }
    };

    const addBullet = (e) => {
        e.stopPropagation();
        const newBullets = [...bullets, { text: '' }];
        setBullets(newBullets);
        onContentChange(JSON.stringify(newBullets));
        // Start editing the new bullet
        setIsEditing(true);
        setEditingBulletIndex(newBullets.length - 1);
    };

    const removeBullet = (e, index) => {
        e.stopPropagation();
        if (bullets.length > 1) {
            const newBullets = bullets.filter((_, i) => i !== index);
            setBullets(newBullets);
            onContentChange(JSON.stringify(newBullets));
        }
    };

    // Create a style object for the text content
    const textStyle = {
        fontSize: fontSize ? `${fontSize}px` : undefined,
        fontWeight,
        fontStyle,
        textDecoration,
        color
    };

    // Parse border radius values or use defaults
    const topLeft = borderRadius?.topLeft || 0;
    const topRight = borderRadius?.topRight || 0;
    const bottomLeft = borderRadius?.bottomLeft || 0;
    const bottomRight = borderRadius?.bottomRight || 0;

    // Create style object for the container
    const containerStyle = {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        overflow: 'hidden',
        position: 'relative',
        borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
        direction: textDirection,
    };

    // Create style for the background with opacity
    const backgroundStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: backgroundColor || 'transparent',
        opacity: opacity,
        borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
        zIndex: -1, // Ensure background is behind the bullets content
    };

    return (
        <div 
            className={`BulletsComponent ${isSelected ? 'selected' : ''}`}
            onClick={handleClick}
        >
            <div className="bullets-container" style={containerStyle}>
                {/* Background div with opacity */}
                <div style={backgroundStyle}></div>


                <ul className="bullets-list" style={{ direction: textDirection }}>
                    {bullets.map((bullet, index) => {
                        // Only show bullet if:
                        // 1. We're in edit mode and editing this bullet, OR
                        // 2. We're not in typing animation mode (show all bullets), OR
                        // 3. We're in typing animation mode and this bullet has started animating (has content in displayedBullets)
                        const isAIGenerating = courseEditorContext?.isGeneratingChapter;
                        const isShowingAIAnimations = courseEditorContext?.isShowingAIChapterAnimations;
                        const aiGenerationStatus = courseEditorContext?.aiGenerationStatus?.status;
                        const isStudentViewMode = mode === "view";
                        const isAICurrentlyGenerating = isAIGenerating || (aiGenerationStatus === 'IN_PROGRESS');

                        const shouldShowTypingAnimation = !isStudentViewMode && 
                                                        (isAICurrentlyGenerating || isSequentialAnimationActive) && 
                                                        bullets.length > 0 && 
                                                        aiGenerationStatus !== 'COMPLETED';

                        const shouldShowBullet = (isEditing && editingBulletIndex === index) || 
                                               !shouldShowTypingAnimation || 
                                               (displayedBullets[index]?.text && displayedBullets[index].text.length > 0);

                        if (!shouldShowBullet) {
                            return null;
                        }

                        return (
                            <li key={index} className="bullet-item">
                                {isEditing && editingBulletIndex === index ? (
                                    <textarea
                                        ref={textareaRef}
                                        value={bullet.text}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        onKeyDown={handleKeyDown}
                                        className="bullet-editor"
                                        placeholder={i18n.translate('weezbeeCourse.editor.bulletsComponent.placeholder', { defaultValue: 'Enter bullet text...' })}
                                        style={{...textStyle, direction: textDirection}}
                                    />
                                ) : (
                                    <div
                                        className="bullet-content"
                                        onDoubleClick={(e) => {
                                            e.stopPropagation();
                                            if (mode === "edit") {
                                                // Enter editing mode for this bullet
                                                setIsEditing(true);
                                                setEditingBulletIndex(index);
                                            }
                                        }}
                                        style={{ direction: textDirection }}
                                    >
                                        {bullet.text ? (
                                            <span style={textStyle}>
                                                {displayedBullets[index]?.text || bullet.text}
                                                {animatingBullets.has(index) && <span className="ai-typing-cursor">|</span>}
                                            </span>
                                        ) : (mode === "edit" && isSelected) ? (
                                            <span className="placeholder">{i18n.translate('weezbeeCourse.editor.bulletsComponent.emptyState', { defaultValue: 'Empty bullet' })}</span>
                                        ) : null}

                                        {/* Only show remove button when selected in edit mode */}
                                        {mode === "edit" && isSelected && (
                                            <button
                                                className="remove-bullet-button"
                                                onClick={(e) => removeBullet(e, index)}
                                                title={i18n.translate('weezbeeCourse.editor.bulletsComponent.removeBullet', { defaultValue: 'Remove bullet' })}
                                            >
                                                <RemoveCircleOutlineIcon fontSize="small" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>

                {/* Only show add button when selected in edit mode */}
                {mode === "edit" && isSelected && (
                    <button
                        className="add-bullet-button"
                        onClick={addBullet}
                        title={i18n.translate('weezbeeCourse.editor.bulletsComponent.addBullet', { defaultValue: 'Add bullet' })}
                    >
                        <AddCircleOutlineIcon fontSize="small" />
                        <span>{i18n.translate('weezbeeCourse.editor.bulletsComponent.addBullet', { defaultValue: 'Add bullet' })}</span>
                    </button>
                )}
            </div>
        </div>
    );
};
